import React, { useRef, useState, useMemo } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import RangePicker from '@home/components/RangePicker';
import GranularitySelect from './components/GranularitySelect';
import DateSelect from './components/DateSelect';
import useDisableInputFocus from './_hooks/useDisableInputFocus';
import type { IBaseProps, INullableMoment } from '@home/types/common';
import type { IGranularity, IDateValue } from './types';
import YearRangePicker from '@gp/components/TeaRangePicker';
import './index.scss';

export interface IComposedDatePickerValue {
  granularity: IGranularity;
  date: IDateValue;
}

export interface IComposedDatePickerProps extends IBaseProps {
  loading?: boolean;
  isNeedDiff?: boolean;
  range?: {
    month: IDateValue;
    quarter: IDateValue;
    year: IDateValue;
  };
  defaultDate?: {
    month: IDateValue;
    quarter: IDateValue;
    year: IDateValue;
  };
  showGranularitySelect?: boolean;
  value: IComposedDatePickerValue;
  onChange: (payload: IComposedDatePickerValue) => void;
}

const MONTHLY_GRANULATES = ['month'];

const getValueOfRangePicker = (dates?: string[]): [moment.Moment, moment.Moment] | ['', ''] => {
  if (!Array.isArray(dates) || dates.length !== 2) return ['', ''];
  return dates.map((date) => moment(date)) as [moment.Moment, moment.Moment];
};

const getIsValidDateByMonth = (date?: moment.Moment, range?: IDateValue) => {
  if (!Array.isArray(range)) return true;
  const [minDate, maxDate] = range;
  const minDateM: INullableMoment = !minDate ? null : moment(minDate);
  const maxDateM: INullableMoment = !maxDate ? null : moment(maxDate);
  return date?.isBetween(minDateM, maxDateM, 'month') || date?.isSame(minDateM, 'month');
};

const ComposedDatePicker: React.FC<IComposedDatePickerProps> = (props) => {
  const {
    style,
    className,
    loading = true,
    range,
    defaultDate,
    showGranularitySelect = false,
    showShortcut = false,
    value,
    onChange,
    isNeedDiff = false,
  } = props;

  const root = useRef<HTMLDivElement>(null);

  // const [shortcutType, setShortcutType] = useState('');

  const handleGranularityChange = (payload: IGranularity) => {
    const defaultDateValue = defaultDate?.[payload] || ['', ''];
    onChange({
      ...value,
      granularity: payload,
      date: defaultDateValue,
    });
  };

  const handleDateChange = (payload: IDateValue) => {
    // setShortcutType('');
    onChange({
      ...value,
      date: payload,
    });
  };

  useDisableInputFocus({ loading, root });

  // 切换时间快捷键
  // const changeShortcutType = (value: string) => {
  //   setShortcutType(value);
  // };

  const isAllOfTime = useMemo<string>(() => {
    if (showGranularitySelect) {
      const type = value?.granularity || '';
      const valueTemp = value?.date || [];
      const rangeTemp = range || ({} as any);
      const rangeValue = rangeTemp[type] || [];
      if (
        Array.isArray(valueTemp) &&
        valueTemp?.length > 1 &&
        Array.isArray(rangeValue) &&
        rangeValue?.length > 1
      ) {
        // return valueTemp.length === rangeValue.length && valueTemp.every((val, i) => val === rangeValue[i]);
        return (
          moment(valueTemp[0]).isSameOrBefore(rangeValue[0]) &&
          moment(valueTemp[1]).isSameOrAfter(rangeValue[1])
        );
      }
    }
    return false;
  }, [value, showGranularitySelect, range]);

  const diffClassName = useMemo(() => {
    // if (isNeedDiff && shortcutType === 'All of time') return '';
    if (isNeedDiff && !isAllOfTime) return 'dbg-diff-filter-value';
    return '';
  }, [isNeedDiff, isAllOfTime]);

  return (
    <div
      ref={root}
      style={style}
      className={classnames('dbg-calendar-filter-date', className, diffClassName)}
    >
      {showGranularitySelect && (
        <>
          <GranularitySelect
            disabled={false}
            value={value?.granularity}
            onChange={handleGranularityChange}
          />
          <DateSelect
            loading={loading}
            range={range}
            granularity={value?.granularity}
            value={value?.date}
            onChange={handleDateChange}
            showShortcut={showShortcut}
          />
        </>
      )}
      {!showGranularitySelect && (
        <YearRangePicker
          className="search-border dbg-overview-filter__range-picker"
          language="en"
          showShortcut={true}
          showSelectType={false}
          disabledMonth={(date: moment.Moment) => getIsValidDateByMonth(date, range?.month)}
          typeList={MONTHLY_GRANULATES}
          type="month"
          value={getValueOfRangePicker(value?.date)}
          onChange={(payload: string[]) => {
            if (!Array.isArray(payload) || payload.length !== 2) return;
            handleDateChange(payload as IDateValue);
          }}
          shortCutType={'monthlyCalendar'}
          overlayClassName={'dbg-filter-date-picker-show-shortcut'}
        />
      )}
    </div>
  );
};

export default ComposedDatePicker;
